import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/shared/auth';
import { useTenant } from '@/shared/tenant';
import { Loading, PageLoading } from '@/shared/components/feedback';
import type { Permission, UserRole } from '@/shared/api/types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: Permission;
  requiredPermissions?: Permission[];
  requireAllPermissions?: boolean;
  requiredRoles?: UserRole[];
  requireTenant?: boolean;
  redirectTo?: string;
}

export function ProtectedRoute({
  children,
  requiredPermission,
  requiredPermissions = [],
  requireAllPermissions = false,
  requiredRoles,
  requireTenant = false,
  redirectTo = '/login',
}: ProtectedRouteProps) {
  const location = useLocation();
  const { isAuthenticated, isLoading: isAuthLoading, hasAnyPermission, hasAllPermissions, checkRole } = useAuth();
  const { hasValidContext, isLoadingOrganizations, isLoadingClients, isLoadingWorkspaces } = useTenant();

  // Show loading while checking auth
  if (isAuthLoading) {
    return <Loading fullScreen />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    const returnTo = location.pathname + location.search;
    return <Navigate to={`${redirectTo}?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  // Check tenant context if required
  if (requireTenant) {
    const isTenantLoading = isLoadingOrganizations || isLoadingClients || isLoadingWorkspaces;

    if (isTenantLoading) {
      return <PageLoading message="Loading workspace..." />;
    }

    if (!hasValidContext) {
      // User needs to select a workspace - could redirect to workspace selector
      return (
        <div className="flex min-h-[400px] flex-col items-center justify-center p-8 text-center">
          <h2 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
            Select a Workspace
          </h2>
          <p className="text-slate-600 dark:text-slate-400">
            Please select an organization, client, and workspace to continue.
          </p>
        </div>
      );
    }
  }

  // Check role requirements
  if (requiredRoles && requiredRoles.length > 0) {
    if (!checkRole(requiredRoles)) {
      return <Navigate to="/app" replace />;
    }
  }

  // Check permission requirements
  const allPermissions = requiredPermission
    ? [requiredPermission, ...requiredPermissions]
    : requiredPermissions;

  if (allPermissions.length > 0) {
    const hasAccess = requireAllPermissions
      ? hasAllPermissions(allPermissions)
      : hasAnyPermission(allPermissions);

    if (!hasAccess) {
      return <UnauthorizedPage />;
    }
  }

  return <>{children}</>;
}

function UnauthorizedPage() {
  return (
    <div className="flex min-h-[400px] flex-col items-center justify-center p-8 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-error-100 dark:bg-error-900">
        <svg
          className="h-8 w-8 text-error-600 dark:text-error-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
      <h2 className="mb-2 text-xl font-semibold text-slate-900 dark:text-slate-100">
        Access Denied
      </h2>
      <p className="text-slate-600 dark:text-slate-400">
        You don't have permission to access this page.
      </p>
    </div>
  );
}

// HOC for protecting routes
export function withProtectedRoute<P extends object>(
  Component: React.ComponentType<P>,
  options?: Omit<ProtectedRouteProps, 'children'>
) {
  return function ProtectedComponent(props: P) {
    return (
      <ProtectedRoute {...options}>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
}
